import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Sparkles, Check, Zap, Palette, TrendingUp, DollarSign, 
  Shield, Clock, CreditCard, ChevronDown, Star, Users,
  Image as ImageIcon, Download, Award
} from 'lucide-react';
import { CREDIT_PLANS, AFFILIATE_COMMISSION_RATE } from '@/lib/constants';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  const handleGetStarted = () => {
    navigate('/login');
  };

  const faqs = [
    {
      q: "Do unused images roll over?",
      a: "Yes, unused images are valid for 30 days from the date of purchase."
    },
    {
      q: "Can I upgrade anytime?",
      a: "Yes, you can upgrade anytime and pay the difference. Your unused images will carry over."
    },
    {
      q: "Is this safe for ads?",
      a: "Yes, all images are generated for commercial use. You own full rights to use them in your ads."
    },
    {
      q: "How fast is image generation?",
      a: "Most images are generated within 30-60 seconds using our AI technology."
    },
    {
      q: "Can I cancel anytime?",
      a: "Yes, you can cancel your subscription anytime. No hidden charges or long-term commitments."
    }
  ];

  const trustBadges = [
    { icon: Clock, text: "Cancel anytime" },
    { icon: Shield, text: "No hidden charges" },
    { icon: CreditCard, text: "Secure payments" },
    { icon: Zap, text: "Instant access" }
  ];

  const whyChooseUs = [
    { icon: Zap, text: "Create ads in seconds" },
    { icon: Palette, text: "No designer needed" },
    { icon: TrendingUp, text: "Better CTR for social ads" },
    { icon: DollarSign, text: "Cheaper than hiring designers" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Hero Section */}
      <header className="container mx-auto px-4 py-8">
        <nav className="flex items-center justify-between mb-16">
          <div className="flex items-center gap-2">
            <Sparkles className="w-8 h-8 text-purple-400" />
            <span className="text-2xl font-bold text-white">Caption Wizard</span>
          </div>
          <div className="flex items-center gap-4">
            <Button variant="ghost" className="text-gray-300 hover:text-white" onClick={() => navigate('/login')}>
              Login
            </Button>
            <Button className="bg-purple-600 hover:bg-purple-700" onClick={handleGetStarted}>
              Get Started Free
            </Button>
          </div>
        </nav>

        <div className="text-center max-w-4xl mx-auto mb-16">
          <Badge className="mb-4 bg-purple-500/20 text-purple-300 border-purple-500/30">
            ⚡ AI-Powered Ad Image Generator
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Create Stunning Ad Images in <span className="text-purple-400">Seconds</span>
          </h1>
          <p className="text-xl text-gray-300 mb-8">
            Generate professional ad images for Instagram, Facebook & more. No design skills needed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-purple-600 hover:bg-purple-700 text-lg px-8" onClick={handleGetStarted}>
              Start Creating Free
              <Sparkles className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/10">
              View Examples
            </Button>
          </div>
          <p className="text-gray-400 mt-4">✨ 5 free images on signup • No credit card required</p>
        </div>
      </header>

      {/* Why Choose Us */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">✅ Why Choose Us?</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {whyChooseUs.map((item, i) => (
            <div key={i} className="text-center p-4">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-purple-500/20 flex items-center justify-center">
                <item.icon className="w-6 h-6 text-purple-400" />
              </div>
              <p className="text-gray-300">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section className="container mx-auto px-4 py-16" id="pricing">
        <h2 className="text-3xl font-bold text-white text-center mb-4">💳 Paid Plans</h2>
        <p className="text-gray-400 text-center mb-12">Choose the plan that works best for your content creation needs</p>
        
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {CREDIT_PLANS.map((plan) => (
            <Card 
              key={plan.id}
              className={`bg-white/5 border-white/20 relative overflow-hidden ${
                plan.popular ? 'ring-2 ring-purple-500 scale-105' : ''
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0">
                  <Badge className="rounded-none rounded-bl-lg bg-purple-600">
                    <Star className="w-3 h-3 mr-1" /> Most Popular
                  </Badge>
                </div>
              )}
              <CardHeader>
                <div className="flex items-center gap-2 mb-2">
                  {plan.id === 'starter' && <span className="text-2xl">🟢</span>}
                  {plan.id === 'creator' && <span className="text-2xl">⭐</span>}
                  {plan.id === 'pro' && <span className="text-2xl">🟣</span>}
                  <CardTitle className="text-white text-xl">{plan.name}</CardTitle>
                </div>
                <CardDescription className="text-gray-400">{plan.description}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-4xl font-bold text-white">₹{plan.price}</span>
                  <span className="text-gray-400"> / {plan.period}</span>
                </div>
                
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-gray-300">
                      <Check className="w-4 h-4 text-green-400" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <Button 
                  className={`w-full ${plan.popular ? 'bg-purple-600 hover:bg-purple-700' : 'bg-white/10 hover:bg-white/20'}`}
                  onClick={handleGetStarted}
                >
                  {plan.id === 'starter' ? 'Get Started' : plan.id === 'creator' ? 'Upgrade Now' : 'Go Pro'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Trust Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">🔒 Trust & Security</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
          {trustBadges.map((badge, i) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-white/5 rounded-lg border border-white/10">
              <badge.icon className="w-5 h-5 text-green-400" />
              <span className="text-gray-300">{badge.text}</span>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="container mx-auto px-4 py-16">
        <h2 className="text-3xl font-bold text-white text-center mb-12">❓ Frequently Asked Questions</h2>
        <div className="max-w-2xl mx-auto space-y-4">
          {faqs.map((faq, i) => (
            <div 
              key={i}
              className="bg-white/5 rounded-lg border border-white/10 overflow-hidden"
            >
              <button
                className="w-full p-4 flex items-center justify-between text-left"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span className="text-white font-medium">{faq.q}</span>
                <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-4 pb-4 text-gray-400">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Affiliate CTA */}
      <section className="container mx-auto px-4 py-16">
        <Card className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-500/30 max-w-4xl mx-auto">
          <CardContent className="p-8 text-center">
            <Badge className="mb-4 bg-green-500/20 text-green-300 border-green-500/30">
              💰 Earn {AFFILIATE_COMMISSION_RATE}% Commission
            </Badge>
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Promote & Earn Up to ₹300 Per Sale
            </h3>
            <p className="text-gray-300 mb-6">
              Join our affiliate program. Share your link and earn {AFFILIATE_COMMISSION_RATE}% on every purchase.
            </p>
            <Button size="lg" className="bg-green-600 hover:bg-green-700" onClick={handleGetStarted}>
              Join Affiliate Program
              <DollarSign className="w-5 h-5 ml-2" />
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-white/10">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-400" />
            <span className="text-xl font-bold text-white">Caption Wizard</span>
          </div>
          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Contact</a>
            <a href="#" className="hover:text-white text-green-400">Earn {AFFILIATE_COMMISSION_RATE}%</a>
          </div>
          <p className="text-gray-500 text-sm">© 2025 Caption Wizard. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
